import Vue from "vue"
import { getDefaultState } from "./state"

const PROVIDER_MUTABLE = [
  "label",
  "count",
  "name",
  "specialization",
  "image",
  "filter",
]
const colors = [
  "#1BC8CD",
  "#E28816",
  "#FB603E",
  "#995FE2",
  "#6BE09A",
  "#B7E15D",
  "#FFB11B",
  "#5D7BE8",
  "#D464D7",
  "#F0DB20",
]

export default {
  /**
   * @func message
   * @desc When a WebSocket message is received.
   */
  message(state, message) {
    var source = message.source
    var action = message.action
    var data = message.data

    // console.log(source, action, data.id, state.patient[data.id] != null)

    if (source === "auth") {
      if (action === "insert") {
        Vue.set(state.patient, data.id, data)
      }
      if (action === "modify" && state.patient[data.id]) {
        Vue.set(
          state.patient,
          data.id,
          Object.assign(state.patient[data.id], data)
        )
      }
      if (action === "remove") {
        Vue.delete(state.patient, data.id)
      }
    }
    // If this was an operation on the provider table
    else if (source === "provider") {
      // Update the provider profile
      if (data.provider === state.id && data.id === "profile") {
        state.provider.count = data.count
        // TODO: if there are new doctors/assistants
      }
    }
  },

  /**
   * @func addProvider
   * @desc Set the current provider in the Vuex state.
   */
  addProvider(state, provider) {
    var existing = state.providers.find(
      (p) => p.provider === provider.provider
    )
    if (!existing) state.providers.push(provider)
  },

  addProviderEvents(state, events) {
    var existing = {}
    state.event.forEach((e, index) => {
      existing[e.id] = index
    })
    events.forEach((e) => {
      if (existing[e.id]) state.event.splice(existing[e.id], 1, e)
      else state.event.push(e)
    })
  },

  addProviderEvent(state, event) {
    var index = state.event.findIndex((e) => e.id === event.id)
    if (index >= 0) state.event.splice(index, 1, event)
    else state.event.push(event)
  },

  addProviderSchedules(state, schedule) {
    state.schedule = state.schedule.concat(schedule)
  },

  /**
   * Sets schedule event id to provider map.
   * To be used in imports for matching events
   *
   * @param {*} state
   * @param {*} eventProviderMap
   */
  addscheduledEventMap(state, eventProviderMap) {
    state.scheduledEventMap = eventProviderMap
  },

  /**
   * Add patient to provider count while checking if the added patients already exist
   *
   * @param {*} state
   * @param {*} patients
   */
  addProviderCount(state, patients) {
    state.provider.count.patient += patients.filter(
      (patient) => !state.patient.hasOwnProperty(patient.id)
    ).length
  },

  addProviderTimezone(state, timezone) {
    state.timezone = timezone
  },

  /**
   * @func setProvider
   * @desc Set the current provider in the Vuex state.
   */
  setProvider(state, provider) {
    if (provider == null || typeof provider !== "object") return;

    state.provider = provider
    localStorage.setItem("provider", provider.provider);
    if (!state.providers.some((p) => p.provider == provider.provider))
      state.providers.push(provider)
  },

  /**
   * @func setProviderId
   * @desc Set the current provider in the Vuex state.
   */
  setProviderId(state, id) {
    if (!id) return

    state.id = id
  },

  /**
   * @func updateProvider
   * @desc Set the current provider in the Vuex state.
   */
  updateProvider(state, provider) {
    if (state.provider && state.provider.provider === provider.provider) {
      // Update specific mutable fields of the provider
      PROVIDER_MUTABLE.forEach((key) => {
        if (provider[key]) Vue.set(state.provider, key, provider[key]);
      });
    }
  },

  /**
   * @func clearProvider
   * @desc Clears the provider object if transitioning during logout or another provider.
   */
  clearProvider(state) {
    state.provider = null
  },

  /**
   * @func setCurrentProvider
   * @desc Set the current provider and cache the ID.
   */
  setCurrentProvider(state, id) {
    if (id) {
      localStorage.setItem("provider", (state.id = id));
      state.provider = state.providers.find((p) => p.provider == id);
    } else {
      localStorage.removeItem("provider");
      state.id = null;
      state.provider = null;
    }
  },

  resetPatients(state) {
    state.patient = []
    state.allPatients = false
  },

  /**
   * @func addPatient
   * @desc Add the given patient to the state. This can safely be called to  update the patient as well.
   */
  addPatient(state, patient) {
    var patientState = state.patient[patient.id]
    Vue.set(
      state.patient,
      patient.id,
      patientState ? Object.assign(patientState, patient) : patient
    )
  },

  addPatientAnalytics(state, { id, analytics }) {
    state.patientAnalytics = {
      [id]: analytics
    }
  },

  /**
   * @func addAssistant
   * @desc Add the given assistant to the state. This can safely be called to  update the assistant as well.
   */
  addAssistant(state, assistant) {
    Vue.set(state.assistant, assistant.id, assistant)
    state.provider.assistant.push(assistant)
  },

  /**
   * Update the patient in the state from the
   * provider patient response (GET /provider/:id/patient/:pid)
   */
  updatePatient(state, response) {
    // Get patient ID from the update
    var id = response.id
    if (!id && response.patient) id = response.patient.id

    if (response.patient) {
      if(!state.patient.id && response.reset) {
        state.patient = {}
        state.record = {
          assessment: {},
          event: {},
          schedule: {},
          message: {},
          questionnaire: {},
          // Whether a patient's complete record has been loaded
          complete: {},
          // Tickets raised by patients to there doctors
          ticket: {},
          // Files uploaded for patient
          file: {},
          // Record uploaded for patients
          record: {},
          // Surveys answered for patient
          survey: {},
          // Charts of saved ctc
          charts: {}
        }
      }
        
      Vue.set(state.patient, id, response.patient)
    } 

    // Update patient records
    Object.keys(state.record).forEach((type) => {
      if (Array.isArray(response[type]) || (type === 'assessment' && response[type] != null)) {
        if (!state.record[type][id])
          Vue.set(state.record[type], id, response[type])
        // Update existing records
        else {
          // Store indexes of existing messages
          let existing = {},
          patientRecord = state.record[type][id]

          patientRecord.forEach((record, index) => {
            existing[record.id] = index
          })

          // Process responses to ensure unique objects in the store
          response[type].forEach((record) => {
            // Replace the record
            if (existing[record.id] != null) {
              patientRecord.splice(existing[record.id], 1, record)
            }
            // Push a new record
            else {
              existing[record.id] = patientRecord.length
              patientRecord.push(record)
            }
          })
        }
      }
      if(type === 'charts') {
        Vue.set(state.record[type], id, response[type])
      }
        
    })

    // If there is no pagination token for provider records, store this is a complete history
    if (!response.next || !response.next.record)
      Vue.set(state.record.complete, id, true)
  },
  /**
   * Add new charting for a single patient  
   * 
   */
  updateChartNote(state, { id, questionnaire_id, chartObj }) {
    if(state.record.charts[id] && state.record.charts[id][questionnaire_id]) {
      state.record.charts[id][questionnaire_id].push(chartObj)
     }
  },

  /**
   * Update the chart push state of a patient questionnaire
   */
  updateChartPushState(state, { id, questionnaire_id, pushedState }) {
    if(state.record.charts[id] && state.record.charts[id][questionnaire_id]) {
      const chartObj = state.record.charts[id][questionnaire_id].pop()
      state.record.charts[id][questionnaire_id].push({
        ...chartObj,
        ...pushedState
      })
    }
  },

  updateSurveyRecord(state, { questionnaire, surveyList }) {
    const prevSurveyList = state.surveyRecord[questionnaire];
    let newSurveyList = surveyList;
    if (prevSurveyList) {
      newSurveyList = [...prevSurveyList];
      surveyList.forEach((surveyObj) => {
        const foundSurveyIndex = prevSurveyList.findIndex(
          (survey) => survey.id === surveyObj.id
        );
        if (foundSurveyIndex !== -1)
          newSurveyList[foundSurveyIndex] = surveyObj;
        else newSurveyList.push(surveyObj);
      });
    }
    console.log(newSurveyList)
    Vue.set(state.surveyRecord, questionnaire, newSurveyList)
  },

  removePatientSchedule(state, schedule) {
    if (state.record.schedule) {
      var schedules = state.record.schedule[schedule.patient];
      var index = schedules.findIndex((s) => s.id == schedule.id);
      if (index >= 0) schedules.splice(index, 1);
    }
  },

  addPatientEvent(state, event) {
    var patientEvents = state.record.event[event.user];

    if (patientEvents) {
      let index = patientEvents.findIndex((e) => e.id === event.id);
      if (index !== -1) patientEvents.splice(index, 1, event);
      else patientEvents.push(event);
    } else {
      state.record.event[event.user] = [event];
    }
  },

  addMessage(state, message) {
    var patientRecord = state.record.message[message.auth];
    // If the record has not yet been initialized for this patient
    if (!patientRecord) Vue.set(state.record.message, message.auth, [message]);
    // If this is a unique schedule object
    else {
      let index = patientRecord.findIndex((record) => record.id === message.id);
      if (index >= 0) patientRecord.splice(index, 1, message);
      else patientRecord.push(message);
    }
  },

  removeMessage(state, message) {
    Object.keys(state.record.message).forEach((id) => {
      let index = state.record.message[id].findIndex(
        (record) => record.id === message.id
      );
      if (index >= 0) state.record.message[id].splice(index, 1);
    });
  },

  addSchedule(state, schedule) {
    var patientRecord = state.record.schedule[schedule.auth];
    // If the record has not yet been initialized for this patient
    if (!patientRecord)
      Vue.set(state.record.schedule, schedule.auth, [schedule]);
    // If this is a unique schedule object
    else {
      let index = patientRecord.findIndex(
        (record) => record.id === schedule.id
      );
      if (index >= 0) patientRecord.splice(index, 1, schedule);
      else patientRecord.push(schedule);
    }
  },

  removeSchedule(state, schedule) {
    Object.keys(state.record.schedule).forEach((id) => {
      let index = state.record.schedule[id].findIndex(
        (record) => record.id === schedule.id
      );
      if (index >= 0) state.record.schedule[id].splice(index, 1);
    });
  },

  /**
   * @func removePatient
   * @desc Update the patient in the state.
   */
  removePatient(state, patient) {
    Vue.delete(
      state.patient,
      typeof patient === "object" ? patient.id : patient
    )
  },

  /**
   * @func receivedPatients
   * @desc Update the patient in the state.
   */
  addPatients(state, patients) {
    patients.forEach((patient) => {
      let patientState = state.patient[patient.id];
      Vue.set(
        state.patient,
        patient.id,
        patientState ? Object.assign(patientState, patient) : patient
      );
    });
  },

  addTickets(state, response) {
    const providerIndex = state.tickets.findIndex(
      (provider) => provider.id === response.id
    );
    if (providerIndex !== -1) {
      Vue.set(state.tickets, providerIndex, response);
    } else state.tickets.push(response);
  },

  addTicketsConfig(state, ticketsConfig) {
    state.ticketsConfig = ticketsConfig
  },

  updateTicket(state, newTicket) {
    const ticketIndex = state.tickets.findIndex(
      (ticket) => ticket.id === newTicket.id
    );
    if (ticketIndex !== -1) Vue.set(state.tickets, ticketIndex, newTicket);
  },

  /**
   * @func receivedPatientEvents
   * @desc Update the patient's longitudinal health record (i.e. event sequence) and remove duplicates.
   */
  setPatientEvents(state, response) {
    // Set the patient in the VueX store if not retrieved yet
    if (!patient) Vue.set(state.patient, response.patient.id, response.patient);

    // Get the patient data
    let patient = state.patient[response.patient.id];
    // If this patient's events have not yet been loaded
    if (!patient.event) Vue.set(patient, "event", response.event);
    // Otherwise insert any duplicates
    else {
      // Create a mapping of existing event IDs to avoid duplication in the store
      let existingId = {};
      patient.event.forEach((e, index) => {
        existingId[e.id] = index;
      });
      // Push unique events onto the patient's event list
      response.event.forEach((e) => {
        // Replace the event with this ID
        if (existingId[e.id]) {
          patient.event.splice(existingId[e.id], 1, e);
        } else {
          patient.event.push(e);
        }
      });
    }
  },

  /**
   * @func receivedPatientMessages
   * @desc Update the patient's message queue.
   */
  setPatientMessages(state, response) {
    // Set the patient in the VueX store if not retrieved yet
    if (!state.patient[response.patient.id])
      Vue.set(state.patient, response.patient.id, response.patient);

    // Get the existing patient
    let patient = state.patient[response.patient.id];
    // If this patient's events have not yet been loaded
    if (!patient.message) Vue.set(patient, "event", response.message);
    // Otherwise insert any duplicates
    else {
      // Create a mapping of existing event IDs to avoid duplication in the store
      let existingId = {};
      patient.message.forEach((e) => {
        existingId[e.id] = true;
      });
      // Push unique events onto the patient's event list
      patient.message.push.apply(
        patient.message,
        response.message.filter((e) => !existingId[e.id])
      );
    }
  },

  addProviderPatientUpdate(state, response) {
    const providerIndex = state.providerPatients.findIndex(
      (provider) => provider.id === response.id
    );
    if (providerIndex !== -1) {
      const patientList = state.providerPatients[providerIndex].patient
      Vue.set(state.providerPatients, providerIndex, {
        id: response.id,
        patient: [...patientList, ...response.patient],
      });
    } else state.providerPatients.push(response);
  },
  createTagToColorMapping(state, patients) {
    // todo: check if tags are already present in store

    let allTags = {};
    patients
      .filter(({ tag }) => tag)
      .forEach((patient) => {
        patient.tag.forEach((tag) => {
          allTags[tag.id] = tag.desc || tag.description;
        });
      });
    Object.entries(allTags).forEach((tag, index) => {
      let reqObj = {
        id: tag[0],
        name: tag[1],
        color: colors[index],
      };
      state.patientTagToColorMapping.push(reqObj);
    });
  },

  addProviderPatients(state, response) {
    const providerIndex = state.providerPatients.findIndex(
      (provider) => provider.id === response.id
    );
    const today = new Date().setHours(0, 0, 0, 0);
    response.patient = response.patient.map((patient) => {
      if (patient.next_schedule)
        patient.next_schedule =
          patient.next_schedule >= today ? patient.next_schedule : null;
      return patient;
    });
    if (providerIndex !== -1) {
      // Gets a map of all patient by id
      const patientMap = state.providerPatients[providerIndex].patient.reduce(
        (acc, patient) => ({ ...acc, [patient.id]: patient }),
        {}
      );
      // Patients that are new in the response
      const additionalPatients = response.patient.filter(
        (patient) => !patientMap.hasOwnProperty(patient.id)
      );
      // Adding new information to existing patients from the response
      response.patient
        .filter((patient) => patientMap.hasOwnProperty(patient.id))
        .forEach(
          (patient) =>
            (patientMap[patient.id] = { ...patientMap[patient.id], ...patient })
        );
      const providerPatients = [
        ...additionalPatients,
        ...Object.values(patientMap),
      ];
      Vue.set(state.providerPatients, providerIndex, {
        id: response.id,
        patient: providerPatients,
      });
    } else {
      state.providerPatients.push(response);
    }
  },
  /**
   * Updates upcoming events to provider patient
   * list for sorting through appointments
   * @param {*} state
   * @param {*} patientInfo
   */
  updateProviderUpcoming(state, patientInfo) {
    const providerIndex = state.providerPatients.findIndex(
      (provider) => provider.id === patientInfo.provider
    );
    if (providerIndex !== -1) {
      const patientIndex = state.providerPatients[
        providerIndex
      ].patient.findIndex((patient) => patient.id === patientInfo.patient);
      if (patientIndex !== -1) {
        const patientTime =
          state.providerPatients[providerIndex].patient[patientIndex]
            .next_schedule;
        let scheduleTime;
        if (!patientInfo.schedule_time || !patientTime)
          scheduleTime = patientInfo.schedule_time || patientTime;
        else
          scheduleTime =
            patientInfo.schedule_time < patientTime
              ? patientInfo.schedule_time
              : patientTime;
        Vue.set(
          state.providerPatients[providerIndex].patient[patientIndex],
          "next_schedule",
          scheduleTime
        );
      }
    }
  },

  /**
   * @func loadNextPatients
   * @desc Store the pagination query
   */
  loadNextPatients(state, query) {
    state.patientNextQuery = query
  },

  loadNextSchedules(state, query) {
    state.scheduleNextQuery = query
  },

  /**
   * @func receivedNextPatients
   * @desc Update the patient in the state.
   */
  loadedAllPatients(state) {
    state.patientNextQuery = false
    state.allPatients = true
  },

  loadedAllSchedules(state) {
    state.scheduleNextQuery = false
    state.allSchedules = true
  },

  cacheProfile(state, id) {
    if (!state.profile.includes(id)) state.profile.push(id)
  },

  addFilter(state, { id, filter }) {
    if (Array.isArray(filter)) Vue.set(state.filters, id, filter)
    else if (state.filters[id]) {
      Vue.set(state.filters, id, [...state.filters[id], filter])
    }
  },

  removeFilter(state, filter) {
    // TODO: handled by websocket
  },
  /**
   * Reset state to default for provider
   * module
   * @param {*} state
   */
  reset(state) {
    Object.assign(state, getDefaultState())
  },

  setFilterArgs(state, { id, arg }) {
    Vue.set(state.filterArg, id, arg)
  },

  setReminderTime(state, { patient, message }) {
    if (
      state.record &&
      state.record.message &&
      Array.isArray(state.record.message[patient])
    ) {
      const messageIndex = state.record.message[patient].findIndex(
        ({ id }) => message === id
      );
      if (messageIndex !== -1)
        Vue.set(
          state.record.message[patient][messageIndex],
          "reminder",
          Date.now()
        );
    }
  },
  setProviderOverviewSite(state, siteOverview) {
    state.siteOverview = siteOverview
  },
  setProviderOverview(state, overview) {
    state.overview = overview
  },
  setAllPatientCount(state, allPatientCount) {
    state.allPatientCount = allPatientCount
  },
  addProviderPushIntegration(state, provider) {
    state.pushIntegration.push(provider)
  },

  /**
   * LOADING STATES
   */

  // Loading the provider object
  startLoading: (state) => {
    state.loading = true
  },
  finishLoading: (state) => {
    state.loading = false
  },

  // Loading patients
  startLoadingEvents: (state) => {
    state.loadingEvents = true
  },
  finishLoadingEvents: (state) => {
    state.loadingEvents = false
  },

  // Loading patients
  startLoadingSchedules: (state) => {
    state.loadingSchedules = true
  },
  finishLoadingSchedules: (state) => {
    state.loadingSchedules = false
  },

  // Loading patients
  startLoadingPatients: (state) => {
    state.loadingPatients = true
  },
  finishLoadingPatients: (state) => {
    state.loadingPatients = false
  },

  // Loading lineup patients
  startLoadingLineup: (state) => {
    state.loadingLineup = true
  },
  finishLoadingLineup: (state) => {
    state.loadingLineup = false
  },

  // Loading Provider Overview
  startLoadingOverview: (state) => {
    state.loadingOverview = true
  },
  finishLoadingOverview: (state) => {
    state.loadingOverview = false
  },

  // Loading Patients for all providers
  startLoadingAllProviders: (state) => {
    state.loadingAllProviders = true
    state.loadedAllProviders = false
  },
  finishLoadingAllProviders: (state) => {
    state.loadingAllProviders = false
    state.loadedAllProviders = true
  },

  // Loading Patients for all providers
  startLoadingAllProviderPatients: (state) => {
    state.loadedAllProviderPatients = false
    state.loadingAllProviderPatients = true
  },
  finishLoadingAllProviderPatients: (state) => {
    state.loadedAllProviderPatients = true
    state.loadingAllProviderPatients = false
  },
  startLoadingAllProviderLinedownPatients: (state) => {
    state.loadedAllProviderLinedownPatients = false
    state.loadingAllProviderLinedownPatients = true
  },
  finishLoadingAllProviderLinedownPatients: (state) => {
    state.loadedAllProviderLinedownPatients = true
    state.loadingAllProviderLinedownPatients = false
  },
  finishLoadingAllProviderEvents: (state) => {
    state.loadedAllProviderEvents = true
  },

  addNextPatients: (state, { provider, next }) => {
    Vue.set(state.nextPatients, provider, next)
  },
  startSurvey: (state, id) => {
    Vue.set(state.surveyState, id, true)
  },

  finishSurvey: (state, id) => {
    Vue.set(state.surveyState, id, false)
  },
  // Loading all Tickets
  startLoadingTickets: (state, id) => {
    state.loadingTickets = true
  },
  finishLoadingTickets: (state, id) => {
    state.loadingTickets = false
  },

  // Loading one patient's data
  startLoadingPatient: (state, id) => {
    Vue.set(state.loadingPatient, id, true)
  },
  finishLoadingPatient: (state, id) => {
    Vue.delete(state.loadingPatient, id)
    Vue.set(state.loadedPatient, id, true)
  },

  // Loading one patient's data
  startUpdatingPatient: (state, id) => {
    Vue.set(state.updatingPatient, id, true)
  },
  finishUpdatingPatient: (state, id) => {
    Vue.delete(state.updatingPatient, id)
  },

  // Loading scheduling
  startSchedulingPatient: (state, id) => {
    Vue.set(state.schedulingPatient, id, true)
  },
  finishSchedulingPatient: (state, id) => {
    Vue.delete(state.schedulingPatient, id)
  },

  // Reminding patient
  startSendingReminder: (state) => {
    state.remindingPatient = true
  },
  finishSendingReminder: (state) => {
    state.remindingPatient = false
  },

  // Notifying patient about app download and permission
  startSendingNotification: (state) => {
    state.notifyingPatient = true
  },
  finishSendingNotification: (state) => {
    state.notifyingPatient = false
  },

  // Messaging patients
  startMessagingPatient: (state, id) => {
    Vue.set(state.messagingPatient, id, true)
  },
  finishMessagingPatient: (state, id) => {
    Vue.delete(state.messagingPatient, id)
  },

  // Bulk Messaging patients
  startMessagingPatientBulk: (state) => {
    state.messagingPatientBulk = true
  },
  finishMessagingPatientBulk: (state) => {
    state.messagingPatientBulk = false
  },

  // Loading Ticketing
  startTicketingPatient: (state, id) => {
    Vue.set(state.ticketingPatient, id, true)
  },
  finishTicketingPatient: (state, id) => {
    Vue.delete(state.ticketingPatient, id)
  },

  // Creating new patients
  startCreatingPatient: (state) => {
    state.creatingPatient = true
  },
  finishCreatingPatient: (state) => {
    state.creatingPatient = false
  },

  // Creating assistants
  startInvitingAssistant: (state) => {
    state.invitingAssistant = true
  },
  finishInvitingAssistant: (state) => {
    state.invitingAssistant = false
  },

  // Creating/editing/removing filters
  startEditingFilter: (state) => {
    state.editingFilter = true
  },
  finishEditingFilter: (state) => {
    state.editingFilter = false
  },

  // Testing filters
  startTestingFilter: (state) => {
    state.testingFilter = true
  },
  finishTestingFilter: (state) => {
    state.testingFilter = false
  },

  // Loading records
  startLoadingRecords: (state) => {
    state.loadingRecords = true
  },
  finishLoadingRecords: (state) => {
    state.loadingRecords = false
  },

  // Date filter for patient
  startFilterPatient(state, date) {
    Vue.set(state.filterTimestamp, "startDate", date.start)
    Vue.set(state.filterTimestamp, "endDate", date.end)
  },

  //uploading excel file
  uploadFileInit: (state) => {
    state.fileUploading = true
    state.fileUploadingError = false
    state.fileUploadSuccess = false
  },
  uploadFileError: (state, error) => {
    state.fileUploadingError = error
    state.fileUploading = false
    state.fileUploadSuccess = false
  },
  uploadFileSuccess: (state, result) => {
    state.fileUploadingError = null
    state.fileUploading = false
    state.fileUploadSuccess = result
  },
  setUploadKey: (state, key) => {
    state.fileUploadKey = key
  },
  setImportErrors: (state, payload) => {
    state.importErrors = payload
  },
  setImportMapping: (state, payload) => {
    state.importMapping = payload
  },
  setRecordIdsForIgnoringImportError: (state, payload) => {
    state.ignoringErrorIds = payload
  },
  // ignoringImportErrorSuccess: (state, payload) => {
  //     state.importErrors = state.importErrors.filter((record) => !payload.find(r => r === record.id))
  // },
  toggleLoadingImportErrors: (state, payload) => {
    state.loadingImportErrors = payload
  },
  setResolvedErrorCount: (state, payload) => {
    state.resolvedErrorCount = payload
  },
}
